import { useState, useMemo, useDeferredValue } from 'react';

export interface FilterConfig<T> {
  searchFields?: (keyof T | string)[];
  multiSelectFilters?: {
    [key: string]: {
      field: keyof T | string;
      matchLogic: 'any' | 'all';
    };
  };
  rangeFilters?: {
    [key: string]: {
      field: keyof T;
      type: 'date' | 'number';
    };
  };
  toggleFilters?: {
    [key: string]: (item: T) => boolean;
  };
  sortableFields?: (keyof T)[];
}

export interface FilterState {
  search?: string;
  [key: string]: any;
}

export interface GenericFiltersResult<T> {
  filters: FilterState;
  filteredItems: T[];
  activeFilterCount: number;
  updateFilters: (updates: Partial<FilterState>) => void;
  clearFilters: () => void;
  setSearch: (query: string) => void;
  setSorting: (field: string, order: 'asc' | 'desc') => void;
}

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Generic filtering and sorting hook
 * Provides reusable filtering logic with search, multi-select, range, and toggle filters
 * 
 * @param items - Array of items to filter
 * @param config - Filter configuration defining searchFields, multiSelectFilters, etc.
 * @returns Filtered items, filter state, and control functions
 * 
 * @example
 * const { filteredItems, filters, updateFilters } = useGenericFilters(companies, {
 *   searchFields: ['name', 'industry'],
 *   multiSelectFilters: {
 *     status: { field: 'status', matchLogic: 'any' }
 *   }
 * });
 */
export function useGenericFilters<T>(
  items: T[],
  config: FilterConfig<T>
): GenericFiltersResult<T> {
  const [filters, setFilters] = useState<FilterState>({});
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Debounce search for better performance
  const deferredSearch = useDeferredValue(filters.search);

  const updateFilters = (updates: Partial<FilterState>) => {
    setFilters(prev => ({ ...prev, ...updates }));
  };

  const clearFilters = () => {
    setFilters({});
    setSortField(null);
    setSortOrder('asc');
  };

  const setSearch = (query: string) => {
    updateFilters({ search: query });
  };

  const setSorting = (field: string, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
  };

  const activeFilterCount = useMemo(() => {
    let count = 0;
    
    // Count search
    if (filters.search?.trim()) count++;
    
    // Count multi-select filters
    Object.keys(config.multiSelectFilters || {}).forEach(key => {
      const value = filters[key];
      if (Array.isArray(value) && value.length > 0) count++;
    });
    
    // Count range filters
    Object.keys(config.rangeFilters || {}).forEach(key => {
      const value = filters[key];
      if (value?.from || value?.to || value?.min !== undefined || value?.max !== undefined) count++;
    });
    
    // Count toggle filters
    Object.keys(config.toggleFilters || {}).forEach(key => {
      if (filters[key] === true) count++;
    });
    
    return count;
  }, [filters, config]);

  const filteredItems = useMemo(() => {
    let result = [...items];

    // Apply search filter with debounced value
    if (deferredSearch?.trim() && config.searchFields) {
      const searchLower = deferredSearch.toLowerCase();
      result = result.filter(item => {
        return config.searchFields!.some(field => {
          const value = getNestedValue(item, field as string);
          return value?.toString().toLowerCase().includes(searchLower);
        });
      });
    }

    // Apply multi-select filters
    if (config.multiSelectFilters) {
      Object.entries(config.multiSelectFilters).forEach(([key, filterConfig]) => {
        const selectedValues = filters[key];
        if (Array.isArray(selectedValues) && selectedValues.length > 0) {
          result = result.filter(item => {
            const itemValue = getNestedValue(item, filterConfig.field as string);
            
            if (filterConfig.matchLogic === 'all') {
              // Item must have ALL selected values (for tags, etc.)
              if (Array.isArray(itemValue)) {
                return selectedValues.every(val => itemValue.includes(val));
              }
              return selectedValues.includes(itemValue);
            } else {
              // Item must have ANY of the selected values (OR logic)
              if (Array.isArray(itemValue)) {
                return selectedValues.some(val => itemValue.includes(val));
              }
              return selectedValues.includes(itemValue);
            }
          });
        }
      });
    }

    // Apply range filters
    if (config.rangeFilters) {
      Object.entries(config.rangeFilters).forEach(([key, filterConfig]) => {
        const range = filters[key];
        if (!range) return;

        result = result.filter(item => {
          const value = item[filterConfig.field];
          
          if (filterConfig.type === 'date') {
            const dateValue = value ? new Date(value as any) : null;
            if (!dateValue) return false;
            
            if (range.from && dateValue < new Date(range.from)) return false;
            if (range.to && dateValue > new Date(range.to)) return false;
          } else if (filterConfig.type === 'number') {
            const numValue = Number(value);
            if (isNaN(numValue)) return false;
            
            if (range.min !== undefined && numValue < range.min) return false;
            if (range.max !== undefined && numValue > range.max) return false;
          }
          
          return true;
        });
      });
    }

    // Apply toggle filters
    if (config.toggleFilters) {
      Object.entries(config.toggleFilters).forEach(([key, filterFn]) => {
        if (filters[key] === true) {
          result = result.filter(filterFn);
        }
      });
    }

    // Apply sorting
    if (sortField) {
      result.sort((a, b) => {
        const aValue = getNestedValue(a, sortField);
        const bValue = getNestedValue(b, sortField);
        
        if (aValue === bValue) return 0;
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        const comparison = aValue < bValue ? -1 : 1;
        return sortOrder === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [items, filters, deferredSearch, sortField, sortOrder, config]);

  return {
    filters,
    filteredItems,
    activeFilterCount,
    updateFilters,
    clearFilters,
    setSearch,
    setSorting,
  };
}
